import { useState } from "react";
import { Trash2 } from "lucide-react";
import { BUILT_IN_PLUGINS } from "@/lib/pluginRegistry";
import { PluginParamSlider, PresetMenu } from "./PresetMenu";

/**
 * LoadedPluginCard — one plugin entry shown on the right side of the
 * PluginManagerPanel. Looks up its param schema by `processorName` against
 * `BUILT_IN_PLUGINS`; falls back to a generic message for custom worklets.
 *
 * iter-146 — extracted from PluginManagerPanel.jsx (was inline, ~65 lines).
 */
export default function LoadedPluginCard({ entry, channelIndex, engine, onRemoved }) {
  const builtIn = BUILT_IN_PLUGINS.find((p) => p.processorName === entry.processorName);
  // Bumping this re-mounts every slider so its useState picks up the new
  // value after a preset load applies `engine.setPluginState()`.
  const [stateBump, setStateBump] = useState(0);
  return (
    <div
      data-testid={`plugin-loaded-${entry.id}`}
      className="rounded-xl border border-cyan-400/30 bg-cyan-500/[0.06] p-3"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="font-display text-[13px] font-semibold leading-tight text-white">
          {entry.label}
        </p>
        <div className="flex items-center gap-2">
          {builtIn && (
            <PresetMenu
              entry={entry}
              channelIndex={channelIndex}
              engine={engine}
              pluginSlug={builtIn.id}
              onLoaded={() => setStateBump((s) => s + 1)}
            />
          )}
          <button
            type="button"
            data-testid={`plugin-remove-${entry.id}`}
            onClick={() => {
              engine.removePluginFromChannel(channelIndex, entry.id);
              onRemoved?.(entry.id);
            }}
            className="grid h-7 w-7 place-items-center rounded-md bg-rose-500/[0.16] text-rose-200 transition hover:bg-rose-500/[0.32]"
            title="Remove plugin"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {builtIn ? (
        <div className="space-y-3">
          {builtIn.params.map((p, paramIdx) => (
            <PluginParamSlider
              // Combine entry.id, param.key, AND positional index — guarantees
              // uniqueness even under React StrictMode double-mounts where
              // the same engine.entry can transiently appear twice.
              key={`${entry.id}-${p.key}-${paramIdx}-${stateBump}`}
              pluginId={entry.id}
              channelIndex={channelIndex}
              paramSchema={p}
              currentValue={entry.params?.[p.key]}
              engine={engine}
            />
          ))}
        </div>
      ) : (
        <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">
          Custom plugin · no schema. The plugin receives transport ticks
          automatically; send params via the engine console.
        </p>
      )}
    </div>
  );
}
